import { resolveSemanticColor } from '@/theme/semanticTokens';
import { r, s } from '@/theme/tokens';
import { Box, Stack, Text, Typography } from '@/ui';
import {
  FileIcon,
  PlayArrowIcon,
  PlayCircleFilledWhiteOutlinedIcon,
  SuitesIcon,
  TestsIcon,
  UploadFile,
} from '@/ui/primitives';
import type { ReactElement } from 'react';

const diagramRows = [
  {
    prefix: '',
    nodes: [{ icon: <UploadFile fontSize="small" />, label: 'Uploads' }],
  },
  {
    prefix: ' └ ',
    nodes: [{ icon: <FileIcon fontSize="small" />, label: 'Results Files' }],
  },
  {
    prefix: '    └ ',
    nodes: [
      {
        icon: <PlayCircleFilledWhiteOutlinedIcon fontSize="small" />,
        label: 'Execution Runs',
      },
    ],
  },
  {
    prefix: '       └ ',
    nodes: [
      { icon: <SuitesIcon fontSize="small" />, label: 'Suites' },
      { icon: <PlayArrowIcon fontSize="small" />, label: 'Suite Runs' },
    ],
  },
  {
    prefix: '          └ ',
    nodes: [
      { icon: <TestsIcon fontSize="small" />, label: 'Tests' },
      { icon: <PlayArrowIcon fontSize="small" />, label: 'Test Runs' },
    ],
  },
] as const;

export const HowFlowQaWorksDiagram = (): ReactElement => (
  <Stack gap="md">
    <Box
      sx={(theme) => ({
        border: `1px solid ${resolveSemanticColor(theme, 'border')}`,
        backgroundColor: resolveSemanticColor(theme, 'pageBg'),
        borderRadius: r('md'),
        p: s('md'),
        width: '100%',
      })}
    >
      <Stack gap="xs" align="stretch">
        {diagramRows.map((row) => (
          <Box
            key={`${row.prefix}${row.nodes.map((node) => node.label).join('-')}`}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Typography
              variant="mono"
              component="span"
              sx={(theme) => ({
                whiteSpace: 'pre',
                color: resolveSemanticColor(theme, 'textSecondary'),
              })}
            >
              {row.prefix}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: row.nodes.length > 1 ? s('none') : s('sm'),
                flexWrap: 'wrap',
              }}
            >
              {row.nodes.map((node, index) => (
                <Box
                  key={node.label}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: s('sm') }}
                >
                  {node.icon}
                  <Text role="body">{node.label}</Text>
                  {index < row.nodes.length - 1 ? (
                    <Typography
                      variant="mono"
                      component="span"
                      sx={(theme) => ({
                        color: resolveSemanticColor(theme, 'textSecondary'),
                        whiteSpace: 'pre',
                      })}
                    >
                      -
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>

    <Stack gap="sm">
      <Text role="body" color="textSecondary">
        Suites and Tests are the stable definitions FlowQA tracks over time.
      </Text>
      <Text role="body" color="textSecondary">
        Suite Runs and Test Runs are the per-execution instances created from each uploaded results
        file.
      </Text>
      <Text role="body" color="textSecondary">
        That structure lets FlowQA compare quality signals across runs instead of treating every
        upload as isolated data.
      </Text>
    </Stack>
  </Stack>
);
